// SENDING_DELAY / BATCH_DELAY / SUCCESS_RATE are deliberately gone (VALE-546).
// They existed only to pace and weight `useSendingStatus.simulateSend`, the
// fabricated 90%-success send that `useSendStep` ran whenever no `onSend`
// handler was supplied. `onSend` is now required and there is no fallback path,
// so there is nothing left to pace and no success rate left to invent.

export const WORKFLOW_STEPS = {
  SELECT_INSTITUTIONS: 'select-institutions',
  SEND_NOTIFICATIONS: 'send-notifications'
} as const;

export const SENDING_STATUS = {
  PENDING: 'pending',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
} as const;

/**
 * Australian financial-institution BRAND colours, keyed by institution name.
 *
 * ── VALE-531 (DARK-7b) — WHY THIS IS ONE HEX PER BRAND AND NOT A CLASS ──────
 * Each entry used to carry five MORE fields — `secondary`, `gradient`,
 * `textColor`, `borderColor`, `bgColor` — hand-derived Tailwind approximations
 * of the `primary` hex sitting beside them. Only `gradient` was ever read
 * (`institution-send-card.tsx`'s 2px brand stripe); the other four were dead.
 *
 * The dark-theme migration is what exposed it: mapping each approximation onto
 * the semantic role its HUE suggested is individually correct and collectively
 * wrong — Westpac, NAB and St.George all became `from-error to-error`, ANZ, BOQ
 * and Australian Super all became `from-info to-info`, so SIX of thirteen brands
 * rendered as two colours, and each `from-*`/`to-*` pair became a gradient with
 * one stop. A brand mark is a categorical set whose whole job is that the
 * members differ, exactly like VALE-524's avatar rotation and matter-type ramp.
 *
 * So the restatement is gone and the stripe is painted from `primary` directly
 * (root CLAUDE.md, "Bind to the source of truth"). Two consequences, both
 * deliberate: a brand colour is THEME-INVARIANT — a bank's red does not invert
 * on a dark ground any more than its logo does, the same reasoning `--scrim`
 * carries over a document preview — and it is invisible to
 * `vale/no-raw-colour-value` because the value reaches the CSS property as an
 * identifier rather than a literal, which is correct: this is brand DATA, not a
 * theming decision.
 */
export const INSTITUTION_BRANDS = {
  'Commonwealth Bank': {
    primary: '#FFCC00' // CBA Yellow
  },
  ANZ: {
    primary: '#007DBA' // ANZ Blue
  },
  Westpac: {
    primary: '#DA1710' // Westpac Red
  },
  NAB: {
    primary: '#ED0000' // NAB Red
  },
  'Bank of Queensland': {
    primary: '#00A9CE' // BOQ Blue
  },
  'Macquarie Bank': {
    primary: '#000000' // Macquarie Black
  },
  'St.George Bank': {
    primary: '#FF0000' // St.George Red (Dragon)
  },
  'Bendigo Bank': {
    primary: '#B4282E' // Bendigo Maroon
  },
  ING: {
    primary: '#FF6200' // ING Orange
  },
  Suncorp: {
    primary: '#00B092' // Suncorp Teal
  },
  Allianz: {
    primary: '#003781' // Allianz Blue
  },
  'Australian Super': {
    primary: '#00A9CE' // Australian Super Cyan
  },
  // Default for non-banks
  default: {
    primary: '#6B7280'
  }
} as const;
